import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from './pagination';
import { cn } from '@av/utils';
import { usePathname, useSearchParams } from 'next/navigation';

export interface PaginatorProps {
  perPage: number;
  totalItems: number;
  maxVisiblePages?: number;
  queryParamName?: string;
}

export function Paginator({
  perPage,
  totalItems,
  maxVisiblePages = 5,
  queryParamName = 'pagina',
}: PaginatorProps) {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const totalPages = perPage && totalItems ? Math.ceil(totalItems / perPage) : 0;
  const currentPage = searchParams.get(queryParamName)
    ? Number(searchParams.get(queryParamName))
    : 1;

  const handlePageChange = (pageNumber: number) => {
    const params = new URLSearchParams(searchParams);
    if (pageNumber > 1) {
      params.set(queryParamName, pageNumber.toString());
    } else {
      params.delete(queryParamName);
    }

    return `${pathname}?${params.toString()}`;
  };

  if (totalPages <= 1) {
    return null;
  }

  const getPageNumbers = (): (number | string)[] => {
    const pageNumbers: (number | string)[] = [];

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      pageNumbers.push(1);

      if (currentPage <= 3) {
        pageNumbers.push(2, 3, 4, '...');
      } else if (currentPage >= totalPages - 2) {
        pageNumbers.push('...', totalPages - 3, totalPages - 2, totalPages - 1);
      } else {
        pageNumbers.push('...', currentPage - 1, currentPage, currentPage + 1, '...');
      }

      pageNumbers.push(totalPages);
    }

    return pageNumbers;
  };

  const pageNumbers = getPageNumbers();

  return (
    <Pagination>
      <PaginationContent className="flex items-center justify-end gap-2 py-3">
        {currentPage > 1 && (
          <PaginationItem>
            <PaginationPrevious
              className="rounded-md text-sm"
              href={handlePageChange(Math.max(1, currentPage - 1))}
            />
          </PaginationItem>
        )}

        {pageNumbers.map((pageNumber, index) => {
          if (pageNumber === '...') {
            return (
              <PaginationItem key={`ellipsis-${index}`}>
                <span className="inline-flex size-10 max-md:size-8 items-center justify-center rounded-md bg-white">
                  <PaginationEllipsis />
                </span>
              </PaginationItem>
            );
          }

          const page = pageNumber as number;

          return (
            <PaginationItem key={page}>
              <PaginationLink
                className={cn(
                  'size-10 max-md:size-8 rounded-md text-base max-md:text-sm',
                  'disabled:opacity-20',
                  currentPage === page
                    ? 'bg-blue-600 text-white hover:text-white'
                    : 'bg-white hover:bg-gray-200'
                )}
                href={handlePageChange(page)}
              >
                {page}
              </PaginationLink>
            </PaginationItem>
          );
        })}

        {currentPage < totalPages && (
          <PaginationItem>
            <PaginationNext
              className="rounded-md text-sm"
              href={handlePageChange(Math.min(totalPages, currentPage + 1))}
            />
          </PaginationItem>
        )}
      </PaginationContent>
    </Pagination>
  );
}
